import _ from 'lodash';

import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Container } from '@material-ui/core';
import { BrokerFavoriteListing } from 'models';
import { COLOR, SERVICE_OFFERS_PUBLIC_PAGE } from 'config/constants';
import { useAuth, useBrokerAllPortalListings } from 'hooks';
import { images } from 'assets/images';
import { EmptyList } from 'pages/shared';
import Fuse from 'fuse.js';
import { HvPleaseWait, HvStepperDots } from 'components';
import { LOADING } from 'state/constants';
import { SearchBar } from 'pages/PersonalPortal/components/Public/components';
import ListingCard from './ListingCard';

const useStyles = makeStyles({
  titlebar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '15px 0',

    '@media (max-width: 991px)': {
      flexDirection: 'column',
    },
  },
  searchInput: {
    boxSizing: 'border-box',

    '@media (min-width: 992px)': {
      maxWidth: '50%',
    },

    '@media (max-width: 991px)': {
      width: '100%',
      marginTop: 10,
    },
  },
  cards: {
    maxHeight: '100%',
    minHeight: 200,
    overflowY: 'auto',
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 300px)',
    justifyContent: 'space-between',
    '@media (max-width: 550px)': {
      justifyContent: 'center',
    },
    width: '100%',
    flexGrow: 1,
    marginTop: 20,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,

    '& .MuiPaginationItem-root': {
      color: COLOR.DARK_GREY,

      '&.Mui-selected, &:hover': {
        color: COLOR.BLACK,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
      },
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
  },
  filterButtonIcon: {
    width: 40,
    height: 40,
  },
  selectedFilterButton: {
    backgroundColor: '#00BDF2',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: '2px solid #F5F5F5',
    width: 60,
    cursor: 'pointer',
    height: 50,
  },
  filterButton: {
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: '2px solid #F5F5F5',
    width: 50,
    cursor: 'pointer',
    height: 50,
  },
  stickyDots: {
    position: 'sticky',
    bottom: 0,
  },
});

interface Props {
  className: string;
  selectedListings: any;
  setSelectedListings: (_: any) => void;
  favorite_listings: [];
}

function ListingList(props: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const { className, selectedListings, setSelectedListings, favorite_listings } = props;
  const fuseOptions = {
    isCaseSensitive: false,
    // includeScore: false,
    // shouldSort: true,
    // includeMatches: false,
    // findAllMatches: false,
    minMatchCharLength: 3,
    // location: 0,
    threshold: 0.2,
    // distance: 100,
    // useExtendedSearch: false,
    ignoreLocation: false,
    // ignoreFieldNorm: false,
    // fieldNormWeight: 1,
    keys: ['address'],
  };
  const { token } = useAuth();
  const { loading, broker_portal_listings } = useBrokerAllPortalListings(token);

  const fuse = new Fuse(broker_portal_listings, fuseOptions);
  const [searchResults, setSearchResults] = useState<BrokerFavoriteListing[]>([]);
  const [filteredBrokerPortalListings, setFilteredBrokerPortalListings] = useState([]);
  const [selected_filters, setSelectedFilters] = useState({
    drone: false,
    video: false,
    floorplan: false,
    blue_hour: false,
    virtual_staging: false,
  });

  const fmtNoListings = intl.formatMessage({ id: 'portal.noListingFound' });

  useEffect(() => {
    setFilteredBrokerPortalListings(broker_portal_listings);
  }, [broker_portal_listings]);

  useEffect(() => {
    const filteredListings = broker_portal_listings.filter((listing) => {
      return Object.keys(selected_filters).every((key) => {
        return (
          !selected_filters[key] ||
          listing.service_offers.some((service_offer) => {
            return SERVICE_OFFERS_PUBLIC_PAGE[key].includes(service_offer.id);
          })
        );
      });
    });
    setFilteredBrokerPortalListings(filteredListings);
  }, [selected_filters]);

  useEffect(() => {
    if (!searchResults.length) {
      setFilteredBrokerPortalListings(broker_portal_listings);
    }
  }, [searchResults]);

  const handleListingSearch = (inputText: string) => {
    if (inputText.length) {
      const results = fuse.search(inputText);
      if (results.length !== 0 && results.length) {
        const filteredListings = results.map((result) => {
          return result.item;
        });
        setSearchResults(filteredListings as BrokerFavoriteListing[]);
        setFilteredBrokerPortalListings(filteredListings);
      } else {
        setSearchResults([]);
        setFilteredBrokerPortalListings([]);
      }
    } else {
      setFilteredBrokerPortalListings(broker_portal_listings);
    }
  };

  return (
    <main className={className}>
      <Box>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <SearchBar
            selected_filters={selected_filters}
            setSelectedFilters={setSelectedFilters}
            setSearchResults={setSearchResults}
            handleListingSearch={handleListingSearch}
          />
        </Box>
        <Box>
          {broker_portal_listings.length ? (
            <Box>
              {filteredBrokerPortalListings.length ? (
                <Box className={styles.cards}>
                  {filteredBrokerPortalListings.map((l: BrokerFavoriteListing) => {
                    return (
                      <ListingCard
                        key={l.id}
                        listingWithServiceOffer={l}
                        selectedListings={selectedListings}
                        setSelectedListings={setSelectedListings}
                        favorite_listings={favorite_listings}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Box>
                  <EmptyList text={fmtNoListings} image={images.empty_listing} />
                </Box>
              )}
            </Box>
          ) : null}
          {loading === LOADING.STARTED ? <HvPleaseWait /> : null}
          {loading === LOADING.FINISHED && !broker_portal_listings.length ? (
            <Box>
              <EmptyList text={fmtNoListings} image={images.empty_listing} />
            </Box>
          ) : null}
          <Box className={styles.stickyDots}>
            <HvStepperDots activeDot={1} numberOfDots={3} />
          </Box>
        </Box>
      </Box>
    </main>
  );
}

ListingList.defaultProps = {
  className: null,
};

ListingList.propTypes = {
  className: PropTypes.string,
};

export default ListingList;
